import React, { useState, Dispatch, SetStateAction } from 'react';
import Text from 'components/text';
import BaseInput from 'components/inputs/base-input';
import { Box, Grid, MenuItem, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import api from 'utils/api';
import TinySpinner from 'components/tiny-spinner';
import { v4 as uuidv4 } from 'uuid';

import { Link } from 'react-router-dom';

import { ReactComponent as Uploaded } from 'assets/icons/uploaded.svg';
import { ReactComponent as Warning } from 'assets/icons/warning.svg';

import { useError } from 'hooks/useError';
import ErrorModal from 'components/error-modal';

import { getIsAllowedExtensions } from 'utils/helpers';
// import ReceiptGuide from './ReceiptGuide';
import PurpleButton from 'components/purple-button';
import { IDealStore } from 'pages/newdeal/store';
import { IReceiptFile } from '..';
import { useTranslation } from 'react-i18next';

const REPORT_STATUS = {
  LOADING: 'loading',
  UPLOADED: 'uploaded',
  DRAFT: 'draft',
};

const ALLOWED_EXTENSIONS = ['xlsx', 'csv', 'tsv', 'cls', 'txt'];

export default function ReceiptsUplaod({
  store,
  setReceipts,
}: {
  store: IDealStore;
  setReceipts: Dispatch<SetStateAction<IReceiptFile | undefined>>;
}) {
  const { t } = useTranslation();
  const { message, setError } = useError();
  const [loading, setLoading] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const {
    state,
    setCurrentStepNumber,
    setReportStatus,
    removeReport,
    addReport,
    replaceTempReport,
  } = store;
  const { currentStepNumber, deal } = state;
  const { reports = [] } = deal || {};
  const hasOneOrManyReports = (reports || []).length > 0;

  async function handleSubmit(event: any) {
    event.preventDefault();
    if (reports.length < 1) {
      setError(t('Please upload your reports.'));
      setOpenErrorModal(true);
    } else {
      setError('');
      setOpenErrorModal(false);
      try {
        setLoading(true);
        const receipts: IReceiptFile = await api.reports.validateLocalReceipts({ id: deal.id });
        setReceipts(receipts);
        setLoading(false);
        setCurrentStepNumber(currentStepNumber + 1);
      } catch (error) {
        setError(
          t(
            'Something went wrong while processing the receipts. Please refresh this page and try again.'
          )
        );
        setLoading(false);
        setOpenErrorModal(true);
      }
    }
  }

  return (
    <Box paddingBottom="80px">
      <Box
        paddingTop={{
          xs: '12px',
          sm: '22px',
        }}
        paddingBottom="32px"
        maxWidth="500px"
        marginX="auto"
      >
        <Text textAlign="center" fontSize="32px" lineHeight="40px" fontWeight="bold">
          {t('Upload Reports')}
        </Text>
        <Text textAlign="center" fontSize="18px" lineHeight="26px" marginTop="8px" color="#484848">
          {t(
            'Upload the last six months of detailed reports covering all tracks you want included in your advance offer.'
          )}
        </Text>
      </Box>

      {hasOneOrManyReports ? (
        <Grid container>
          <Grid item xs={12} md={4} lg={5}>
            <FileUploader
              state={state}
              setError={setError}
              addReport={addReport}
              replaceTempReport={replaceTempReport}
              setReportStatus={setReportStatus}
              setOpenErrorModal={setOpenErrorModal}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            lg={7}
            paddingLeft={{
              xs: '0px',
              md: '40px',
            }}
            paddingTop={{
              xs: '30px',
              md: '0px',
            }}
          >
            <RenderFiles
              deal={deal}
              removeReport={removeReport}
              setReportStatus={setReportStatus}
              setError={setError}
              setOpenErrorModal={setOpenErrorModal}
            />
          </Grid>
        </Grid>
      ) : (
        <Box
          paddingTop={{
            xs: '12px',
            sm: '22px',
          }}
          paddingBottom="32px"
          maxWidth="500px"
          marginX="auto"
        >
          <FileUploader
            state={state}
            setError={setError}
            addReport={addReport}
            replaceTempReport={replaceTempReport}
            setReportStatus={setReportStatus}
            setOpenErrorModal={setOpenErrorModal}
          />
        </Box>
      )}
      <Box maxWidth="320px" marginX="auto" textAlign="center" marginY="100px">
        <PurpleButton disabled={loading} onClick={handleSubmit} sx={{ width: '184px' }}>
          {loading ? (
            <>
              <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              <Text color="#fff" fontWeight="bold" paddingLeft={2}>
                {t('Please wait...')}
              </Text>
            </>
          ) : (
            <Text color="#fff" fontWeight="bold">
              {t('Next')}
            </Text>
          )}
        </PurpleButton>
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            setCurrentStepNumber(2);
          }}
          style={{ textDecoration: 'none' }}
        >
          <Text
            textAlign="center"
            fontSize="18px"
            lineHeight="26px"
            marginTop="20px"
            color="#484848"
          >
            {t('Go back')}
          </Text>
        </Link>
      </Box>
      <ErrorModal
        title="Ops!"
        open={openErrorModal}
        handleClose={() => setOpenErrorModal(false)}
        message={message}
      />
    </Box>
  );
}

function FileUploader(props: any) {
  const { t } = useTranslation();
  const { state, setError, addReport, setReportStatus, setOpenErrorModal, replaceTempReport } =
    props;
  const { deal } = state;
  const [distributorName, setDistributorName] = useState(false);

  const [dragActive, setDragActive] = useState(false);
  const inputRef = React.useRef(null);

  const distributors = [
    { name: 'tunecore', label: 'TuneCore' },
    { name: 'ditto', label: 'Ditto' },
    { name: 'distrokid', label: 'DistroKid' },
    { name: 'cdbaby', label: 'CD Baby' },
    { name: 'other', label: 'Other' },
  ];

  const checkExtensions = (files: any) => {
    const fileNames = [];
    for (const file of files) {
      fileNames.push(file.name);
    }
    const extensionsAreValid = fileNames.every((file: any) =>
      getIsAllowedExtensions(file, ALLOWED_EXTENSIONS)
    );
    return extensionsAreValid;
  };

  const uploadFiles = async (files: any) => {
    if (checkExtensions(files)) {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        // Create a temporary report file.
        const tempId = uuidv4();
        let report: any = {
          id: tempId,
          name: file.name,
          distributor: distributorName,
          status: REPORT_STATUS.LOADING,
          deal_id: deal.id,
          hubspotFileUrl: '#',
          hubspotFileId: 0,
          createdAt: Date.now(),
          url: null,
        };

        addReport(report);

        try {
          report = await api.reports.createInitialReportLocalStorage({
            id: deal.id,
            data: formData,
          });

          replaceTempReport({
            report: {
              ...report,
              distributor: distributorName,
            },
            tempId,
          });
        } catch (e) {
          console.log(e);
          setError(
            t(
              'Something went wrong while processing the report. Please refresh this page and try again.'
            )
          );
          setOpenErrorModal(true);
        }

        if (report !== null) {
          try {
            await api.reports.finaliseReport({
              data: {
                deal_id: deal.id,
                report_id: report.id,
                distributor: distributorName,
                isLocalReport: true,
              },
            });
            setReportStatus(report.id, REPORT_STATUS.UPLOADED);
          } catch (e) {
            setError(
              t(
                'Something went wrong while updating the report. Please refresh this page and try again.'
              )
            );
            setOpenErrorModal(true);
            setReportStatus(report.id, REPORT_STATUS.DRAFT);
          }
        }
      }
    } else {
      setError(
        t(`Uploaded file type is not supported. Supported file types are: {{extensions}}`, {
          extensions: ALLOWED_EXTENSIONS.join(', '),
        })
      );
      setOpenErrorModal(true);
    }
  };

  const handleDrag = function (e: any) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = function (e: any) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  const handleChange = function (e: any) {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      uploadFiles(e.target.files);
    }
  };

  const onButtonClick = () => {
    // @ts-ignore
    inputRef.current.click();
  };

  return (
    <Box>
      <BaseInput
        select
        variant="filled"
        name="distributor"
        label={t('Reports source')}
        defaultValue="placeholder"
        SelectProps={{ displayEmpty: true }}
        sx={{ width: '100%' }}
        onChange={(event: any) => {
          setDistributorName(event.target.value);
        }}
        error={false}
      >
        <MenuItem disabled value="placeholder">
          <Typography sx={{ color: '#444' }}>{t('Select distributor')}</Typography>
        </MenuItem>
        {distributors.map((dist) => (
          <MenuItem key={dist.label} value={dist.name}>
            {dist.label}
          </MenuItem>
        ))}
      </BaseInput>

      {distributorName && (
        <Box
          onDragEnter={handleDrag}
          position="relative"
          border={2}
          borderColor="#DFE1E6"
          marginTop="24px"
          paddingY="50px"
          borderRadius="4px"
          sx={{
            borderStyle: 'dashed',
          }}
        >
          <Box
            display="none"
            component="input"
            ref={inputRef}
            type="file"
            multiple={true}
            key={Date.now()}
            onChange={handleChange}
          />
          <Box component="label">
            <Box textAlign="center">
              <Box
                component="button"
                className="upload-button"
                onClick={onButtonClick}
                paddingY="5px"
                paddingX="24px"
                bgcolor="#000"
                borderRadius="4px"
                border="none"
                marginBottom="8px"
              >
                <Text fontSize="20px" lineHeight="30px" color="#fff">
                  {t('Upload files')}
                </Text>
              </Box>
              <Text fontSize="18px" color="#818181">
                {t('Or drag them in')}
              </Text>
            </Box>
          </Box>
          {dragActive && (
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              position="absolute"
              bgcolor="#ccc"
              top={0}
              bottom={0}
              right={0}
              left={0}
              sx={{
                opacity: 0.3,
              }}
            ></Box>
          )}
        </Box>
      )}

      {/* <ReceiptGuide /> */}
    </Box>
  );
}

function RenderFiles(props: any) {
  const { t } = useTranslation();
  const { deal, removeReport, setReportStatus, setError, setOpenErrorModal } = props;
  const { reports = [] } = deal;

  const handleReportFileDelete = async (report: any) => {
    setReportStatus(report.id, REPORT_STATUS.LOADING);
    try {
      await api.reports.removeFromLocalStorage({ id: report.id });
      removeReport(report.id);
    } catch (e) {
      setError(
        t(
          'Something went wrong while deleting the report file. Please refresh this page and try again.'
        )
      );
      setOpenErrorModal(true);
      setReportStatus(report.id, REPORT_STATUS.DRAFT);
    }
  };

  return (
    <Box
      bgcolor="#FAFBFC"
      border={1}
      borderColor="#DFE1E6"
      borderRadius="8px"
      padding="24px"
      marginBottom="60px"
    >
      <Grid container paddingBottom="16px" borderBottom={1} borderColor="#DFE1E6">
        <Grid item xs={9} md={6}>
          <Text color="#818181" fontSize="18px">
            {t('Name')}
          </Text>
        </Grid>
        <Grid
          display={{
            xs: 'none',
            md: 'block',
          }}
          item
          xs={0}
          md={4}
        >
          <Text color="#818181" fontSize="18px">
            {t('Distributor')}
          </Text>
        </Grid>
        <Grid item xs={3} md={2}></Grid>
      </Grid>

      {reports.map((report: any) => {
        return (
          <Grid container paddingY="16px" key={report.id}>
            <Grid item xs={9} md={6}>
              <Box display="flex">
                {report.status === 'draft' && <Warning title={t('Report file was not uploaded')} />}
                {report.status === 'uploaded' && <Uploaded />}
                {report.status === 'loading' && <TinySpinner />}
                <Text
                  color="#333333"
                  fontSize="18px"
                  marginLeft="16px"
                  width="100%"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  marginRight="16px"
                  title={report.name}
                >
                  {report.name}
                </Text>
              </Box>
            </Grid>
            <Grid
              display={{
                xs: 'none',
                md: 'block',
              }}
              item
              xs={0}
              md={4}
            >
              <Text color="#333333" fontSize="18px">
                {report.distributor}
              </Text>
            </Grid>
            <Grid item xs={3} md={2}>
              {report.status === 'uploaded' && (
                <Text
                  color="#333333"
                  fontSize="18px"
                  textAlign="right"
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                  title={`Delete "${report.name}" report`}
                  onClick={() => handleReportFileDelete(report)}
                >
                  {t('Delete')}
                </Text>
              )}
            </Grid>
          </Grid>
        );
      })}
    </Box>
  );
}
